import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import { ReactComponent as Checked } from '../../../assets/images/svg/checked.svg';
import { ReactComponent as Unchecked } from '../../../assets/images/svg/unchecked.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import { mediaBelow } from '../../../assets/styles/helper';
import { ModalClose } from '../../Modal/Modal';
import BottomDrawer from '../../Drawer/Drawer';
import SfhPrimaryButton from './SfhPrimaryButton';
import SfhSecondaryButton from './SfhSecondaryButton';

// Same absolute ModalClose placement as PendingAccountVerificationModal /
// other centered info modals (top-right of the content area).
const AbsoluteClose = styled(ModalClose)`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  &,
  svg {
    width: 20px;
    height: 20px;
  }

  /* close.svg X spans 8/16 of the viewBox → scales to match 20px */
  svg > g {
    transform: scale(1.25);
    transform-origin: center;
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 0;
  text-align: center;

  > svg:first-of-type {
    width: 36px;
    height: 36px;
  }
`;

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const Message = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};

  > strong {
    font-weight: 700;
    color: ${themeColors.colorTextSecondary};
  }
`;

const Divider = styled.hr`
  width: 100%;
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0;
`;

const NoticeBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 6px;
  padding: 12px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

// Matches the Review step T&C acceptance row (TermsRow / TermsIndicator / TermsText).
const AcknowledgeRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const AcknowledgeCheckboxInput = styled.input`
  display: none;
`;

const AcknowledgeIndicator = styled.span`
  flex-shrink: 0;
  display: inline-flex;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $checked }) =>
      $checked ? themeColors.colorPrimary : themeColors.colorSfhCheckboxBorder};
  }

  ${({ $checked }) =>
    $checked &&
    `
    svg rect {
      fill: currentColor;
      stroke: currentColor;
    }

    svg polyline {
      stroke: ${themeColors.colorWhite};
    }
  `}
`;

const AcknowledgeText = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const DrawerFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  /* Cancel BottomDrawer Footer top padding (16px) only for this notice. */
  margin-top: -16px;
`;

// Desktop: Go Back | Continue side-by-side. Mobile: Continue stacked above Go Back
// (same order swap as SellFromHomeIrsSsnDrawer IrsButtonRow).
const ButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  width: 100%;
  padding-top: 16px;
  button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
  }

  button:disabled,
  button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-template-columns: 1fr;
    grid-gap: 12px;

    button:nth-child(1) {
      order: 2;
    }

    button:nth-child(2) {
      order: 1;
    }
  }
`;

const SellFromHomeShippingNoticeDrawer = ({
  isOpen,
  onClose,
  acknowledged,
  onAcknowledgedChange,
  onContinue,
  showHighValueNotice,
  showDailyLimitNotice,
}) => {
  const { t } = useTranslation();

  const footer = (
    <DrawerFooter>
      <AcknowledgeRow>
        <AcknowledgeCheckboxInput
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => onAcknowledgedChange(event.target.checked)}
          data-cy="checkbox-sfh-shipping-notice"
        />
        <AcknowledgeIndicator $checked={acknowledged}>
          {acknowledged ? <Checked /> : <Unchecked />}
        </AcknowledgeIndicator>
        <AcknowledgeText>
          {t('sellWizard.sfhWizard.shippingNoticeDrawer.checkboxLabel')}
        </AcknowledgeText>
      </AcknowledgeRow>
      <ButtonRow>
        <SfhSecondaryButton
          onClick={onClose}
          data-cy="button-sfh-shipping-notice-go-back"
        >
          {t('sellWizard.sfhWizard.shippingNoticeDrawer.goBack')}
        </SfhSecondaryButton>
        <SfhPrimaryButton
          disabled={!acknowledged}
          onClick={onContinue}
          data-cy="button-sfh-shipping-notice-continue"
        >
          {t('sellWizard.sfhWizard.shippingNoticeDrawer.continue')}
        </SfhPrimaryButton>
      </ButtonRow>
    </DrawerFooter>
  );

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      hideHeader
      hideClose
      footer={footer}
      maxHeight="85%"
      contentPadding="16px"
    >
      <Content>
        <AbsoluteClose
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          data-cy="button-sfh-shipping-notice-close"
        >
          <Close width={20} height={20} />
        </AbsoluteClose>
        <InfoIcon />
        <Title>
          {showHighValueNotice
            ? t('sellWizard.sfhWizard.shippingNoticeDrawer.title')
            : t('sellWizard.sfhWizard.shippingNoticeDrawer.dailyLimitTitle')}
        </Title>
        {showHighValueNotice && (
          <>
            <Message>
              {t(
                'sellWizard.sfhWizard.shippingNoticeDrawer.messageLine1Prefix',
              )}{' '}
              <strong>
                {t(
                  'sellWizard.sfhWizard.shippingNoticeDrawer.messageLine1Bold',
                )}
              </strong>
            </Message>
            <Message>
              {t('sellWizard.sfhWizard.shippingNoticeDrawer.messageLine2')}
            </Message>
          </>
        )}
        {showDailyLimitNotice && showHighValueNotice && (
          <>
            <Divider />
            <NoticeBox data-cy="sfh-shipping-notice-daily-limit-box">
              <AmberAlertTriangle />
              <span>
                {t(
                  'sellWizard.sfhWizard.shippingNoticeDrawer.dailyLimitMessage',
                )}
              </span>
            </NoticeBox>
          </>
        )}
        {showDailyLimitNotice && !showHighValueNotice && (
          <Message>
            {t('sellWizard.sfhWizard.shippingNoticeDrawer.dailyLimitMessage')}
          </Message>
        )}
      </Content>
    </BottomDrawer>
  );
};

SellFromHomeShippingNoticeDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  acknowledged: PropTypes.bool,
  onAcknowledgedChange: PropTypes.func,
  onContinue: PropTypes.func,
  showHighValueNotice: PropTypes.bool,
  showDailyLimitNotice: PropTypes.bool,
};

export default SellFromHomeShippingNoticeDrawer;
